import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BACKEND_API_URL } from '@shared/core';
import { select, Store } from '@ngrx/store';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { getLayoutScopeId } from '../selectors';

/** Stamps every request to our own backend with the org scope (e.g. "CEM") the frontend
 *  resolved in scope.ts, so backend functions don't have to re-derive it from Origin/Referer. */
@Injectable()
export class ScopeInterceptor implements HttpInterceptor {
	constructor(
		private store: Store<any>,
		private afAuth: AngularFireAuth,
		@Inject(BACKEND_API_URL) private backendApiUrl: string
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!req.url.startsWith(this.backendApiUrl)) {
			return next.handle(req);
		}

		return this.afAuth.authState.pipe(
			take(1),
			switchMap(user => {
				const token$ = user ? from(user.getIdToken()) : of(null);
				return token$.pipe(
					switchMap(idToken =>
						this.store.pipe(
							select(getLayoutScopeId),
							take(1),
							switchMap(scope => {
								const headers: Record<string, string> = {};
								if (idToken) headers.Authorization = `Bearer ${idToken}`;
								if (scope) headers['X-Spaq-Scope'] = scope;
								const request = Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req;

								return next.handle(request).pipe(
									catchError(error => {
										if (error instanceof HttpErrorResponse && error.status === 401 && idToken) {
											this.afAuth.signOut().then(() => (window.location.href = '/'));
										}
										return throwError(error);
									})
								);
							})
						)
					)
				);
			})
		);
	}
}
